import { useCallback, useEffect, useState } from "react";

const STORAGE_PREFIX = "adultnet:favorites:";

function readIds(kind: string): string[] {
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${kind}`);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
}

export function useFavorites(kind: string) {
  const [ids, setIds] = useState<string[]>(() => readIds(kind));

  useEffect(() => {
    setIds(readIds(kind));
  }, [kind]);

  const persist = useCallback(
    (next: string[]) => {
      setIds(next);
      try {
        window.localStorage.setItem(`${STORAGE_PREFIX}${kind}`, JSON.stringify(next));
      } catch {
        // storage might be unavailable; keep in-memory state only
      }
    },
    [kind],
  );

  const toggle = useCallback(
    (id: string) => {
      const current = readIds(kind);
      const exists = current.includes(id);
      const next = exists ? current.filter((item) => item !== id) : [...current, id];
      persist(next);
      return !exists;
    },
    [kind, persist],
  );

  const isFavorite = useCallback((id: string) => ids.includes(id), [ids]);

  return { favoriteIds: ids, isFavorite, toggle };
}